import React from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useData } from '../../contexts/DataContext';
import { 
    Users, FileText, CheckCircle, Clock, 
    ArrowUpRight, TrendingUp, Calendar, 
    UserPlus, Activity, ChevronRight, MoreHorizontal, X
} from 'lucide-react';
import { 
    XAxis, YAxis, CartesianGrid, 
    Tooltip, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
    const { profiles, assessments, assignedAssessments } = useData();
    const navigate = useNavigate();
    const [velocityView, setVelocityView] = React.useState('Live');
    const [isCaseloadModalOpen, setCaseloadModalOpen] = React.useState(false);

    const activeEvaluations = assessments.length;
    const pendingAssignments = assignedAssessments.length;

    const growthData = velocityView === 'Live' ? [
        { name: 'Mon', value: 4 },
        { name: 'Tue', value: 7 },
        { name: 'Wed', value: 5 },
        { name: 'Thu', value: 9 },
        { name: 'Fri', value: 12 },
        { name: 'Sat', value: 8 },
        { name: 'Sun', value: 10 },
    ] : [
        { name: 'Week 1', value: 45 },
        { name: 'Week 2', value: 52 },
        { name: 'Week 3', value: 48 },
        { name: 'Week 4', value: 65 },
        { name: 'Week 5', value: 58 },
    ];

    const distributionData = [
        { name: 'Autism Spectrum', value: 45, color: '#0d9488' },
        { name: 'ADHD / ADD', value: 30, color: '#0ea5e9' },
        { name: 'Motor Delay', value: 15, color: '#f59e0b' },
        { name: 'Other Markers', value: 10, color: '#94a3b8' },
    ];

    return (
        <div className="space-y-10">
            {/* Welcome Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <h1 className="text-4xl font-black text-slate-900 font-outfit tracking-tight">Clinical Overview</h1>
                    <p className="text-slate-500 mt-2 text-lg font-light">Managing patient outcomes and institutional growth.</p>
                </div>
                <div className="flex items-center gap-3">
                    <Button onClick={() => navigate('/app/profiles')} className="rounded-2xl h-12 px-6 shadow-premium font-bold bg-brand-600 hover:bg-brand-700 text-white transition-all">
                        <UserPlus className="mr-2 h-5 w-5" /> Add Patient
                    </Button>
                </div>
            </div>

            {/* Metric Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                <DashboardMetric 
                    label="Total Patients" 
                    value={profiles.length} 
                    icon={<Users className="text-brand-600" />} 
                    trend="+12% this month"
                    color="bg-brand-50"
                />
                <DashboardMetric 
                    label="Active Evaluations" 
                    value={activeEvaluations} 
                    icon={<Activity className="text-blue-600" />} 
                    trend="5 ongoing today"
                    color="bg-blue-50"
                />
                <DashboardMetric 
                    label="Pending Reports" 
                    value={pendingAssignments} 
                    icon={<FileText className="text-amber-600" />} 
                    trend="2 high priority"
                    color="bg-amber-50"
                />
                <DashboardMetric 
                    label="Completion Rate" 
                    value="94%" 
                    icon={<CheckCircle className="text-green-600" />} 
                    trend="Top decile performance"
                    color="bg-green-50"
                />
            </div>

            <div className="grid lg:grid-cols-3 gap-10">
                {/* Main Analytics Cart */}
                <Card className="lg:col-span-2 border-0 shadow-soft overflow-hidden rounded-[2.5rem] bg-white border border-slate-50">
                    <CardHeader className="p-10 pb-0">
                        <div className="flex items-center justify-between">
                            <div>
                                <CardTitle className="text-2xl font-outfit font-black text-slate-900">Assessment Velocity</CardTitle>
                                <CardDescription className="text-slate-500 font-medium mt-1">Tracking clinical throughput over the last 7 days</CardDescription>
                            </div>
                            <div className="flex items-center gap-2 bg-slate-50 p-1.5 rounded-2xl border border-slate-100">
                                <button 
                                    onClick={() => setVelocityView('Live')}
                                    className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${velocityView === 'Live' ? 'bg-white shadow-sm text-brand-600' : 'text-slate-400 hover:text-slate-600'}`}>
                                    Live
                                </button>
                                <button 
                                    onClick={() => setVelocityView('History')}
                                    className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${velocityView === 'History' ? 'bg-white shadow-sm text-brand-600' : 'text-slate-400 hover:text-slate-600'}`}>
                                    History
                                </button>
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-10 h-[400px]">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={growthData}>
                                <defs>
                                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.2}/>
                                        <stop offset="95%" stopColor="#14b8a6" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12, fontWeight: 600}} dy={10} />
                                <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 12, fontWeight: 600}} />
                                <Tooltip 
                                     contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.15)', padding: '12px 20px' }}
                                />
                                <Area type="monotone" dataKey="value" stroke="#14b8a6" strokeWidth={4} fillOpacity={1} fill="url(#colorValue)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>

                {/* Sidebar Cards */}
                <div className="space-y-8">
                    {/* Diagnosis Distribution */}
                    <Card className="border-0 shadow-soft rounded-[2.5rem] overflow-hidden bg-white border border-slate-50">
                        <CardHeader className="p-8 pb-0">
                            <CardTitle className="text-xl font-outfit font-black text-slate-900">Caseload Mix</CardTitle>
                        </CardHeader>
                        <CardContent className="p-8">
                            <div className="space-y-6">
                                {distributionData.map((item, idx) => (
                                    <div key={idx} className="space-y-2.5">
                                        <div className="flex justify-between text-xs font-bold uppercase tracking-wider">
                                            <span className="text-slate-500">{item.name}</span>
                                            <span className="text-slate-900">{item.value}%</span>
                                        </div>
                                        <div className="h-2.5 w-full bg-slate-50 rounded-full overflow-hidden border border-slate-100 p-0.5">
                                            <div 
                                                className="h-full rounded-full transition-all duration-1000 shadow-sm" 
                                                style={{ width: `${item.value}%`, backgroundColor: item.color }}
                                            ></div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                             <Button 
                                variant="ghost" 
                                onClick={() => setCaseloadModalOpen(true)}
                                className="w-full mt-10 text-brand-600 font-black text-[10px] uppercase tracking-[0.2em] hover:bg-brand-50 rounded-2xl h-12 transition-all">
                                Detailed Breakdown <ArrowUpRight className="ml-2 h-4 w-4" />
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Quick Access */}
                    <Card className="bg-slate-900 text-white border-0 shadow-premium rounded-[2.5rem] overflow-hidden relative">
                         <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl"></div>
                        <CardContent className="p-10 relative z-10">
                            <div className="h-14 w-14 rounded-2xl bg-white/10 flex items-center justify-center mb-8 border border-white/10 shadow-inner">
                                <TrendingUp className="h-7 w-7 text-brand-400" />
                            </div>
                            <h3 className="text-2xl font-black font-outfit mb-3">Practice Insights</h3>
                            <p className="text-slate-400 text-sm leading-relaxed mb-8 font-light">Your average assessment time has decreased by <span className="text-brand-400 font-bold">15%</span> this quarter while maintaining outcome accuracy.</p>
                             <Button 
                                onClick={() => navigate('/app/reports')}
                                className="w-full bg-white/10 hover:bg-white/20 text-white rounded-2xl h-14 font-black text-xs uppercase tracking-widest border border-white/10 transition-all backdrop-blur-sm">
                                View Strategy Report
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Recent Patients Section */}
            <div className="space-y-8 pt-6">
                <div className="flex items-center justify-between px-2">
                    <h2 className="text-3xl font-black text-slate-900 font-outfit tracking-tight">Active Profiles</h2>
                    <Button variant="ghost" className="text-slate-400 font-black text-xs uppercase tracking-widest hover:text-brand-600 transition-colors" onClick={() => navigate('/app/profiles')}>
                        View All <ChevronRight className="ml-1.5 h-4 w-4" />
                    </Button>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {profiles.slice(0, 3).map((profile) => (
                        <Card key={profile.id} className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 hover:shadow-premium hover:-translate-y-1 transition-all group cursor-pointer overflow-hidden" onClick={() => navigate(`/app/profiles/${profile.id}`)}>
                            <CardContent className="p-8">
                                <div className="flex items-start justify-between mb-6">
                                    <div className="h-16 w-16 rounded-2xl bg-slate-50 flex items-center justify-center text-2xl font-black text-slate-300 group-hover:bg-brand-50 group-hover:text-brand-600 transition-all border border-slate-100 shadow-inner">
                                        {profile.name.charAt(0)}
                                    </div>
                                    <Badge className="rounded-xl px-4 py-1.5 bg-slate-50 text-slate-500 border-slate-100 text-[10px] uppercase font-black tracking-widest">{profile.status}</Badge>
                                </div>
                                <h4 className="text-xl font-black text-slate-900 group-hover:text-brand-600 transition-colors tracking-tight font-outfit">{profile.name}</h4>
                                <p className="text-sm text-slate-500 font-medium mt-1">{profile.diagnosis}</p>
                                <div className="mt-8 pt-6 border-t border-slate-50 flex items-center justify-between">
                                    <div className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 tracking-widest">
                                        <Calendar className="h-3.5 w-3.5" /> Updated 2d ago
                                    </div>
                                    <div className="h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center text-slate-400 group-hover:bg-brand-600 group-hover:text-white transition-all shadow-sm">
                                        <MoreHorizontal className="h-5 w-5" />
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
             </div>

            {/* Caseload Breakdown Modal */}
            {isCaseloadModalOpen && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xl z-[100] flex items-center justify-center p-6">
                    <Card className="w-full max-w-2xl shadow-premium border-0 rounded-[3rem] overflow-hidden bg-white relative">
                        <button 
                            onClick={() => setCaseloadModalOpen(false)}
                            className="absolute top-8 right-8 h-10 w-10 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900 transition-colors z-20"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <CardHeader className="p-10 pb-0">
                            <h2 className="text-3xl font-black text-slate-900 font-outfit uppercase tracking-tight">Clinical Caseload Mix</h2>
                            <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1 italic">Detailed population distribution analysis</p>
                        </CardHeader>
                        <CardContent className="p-10 space-y-8">
                            <div className="grid grid-cols-2 gap-6">
                                {[
                                    { label: 'Level 1 Support', val: '42%' },
                                    { label: 'Level 2 Support', val: '33%' },
                                    { label: 'Level 3 Support', val: '25%' },
                                    { label: 'Post-Discharge', val: '12' },
                                ].map((stat, i) => (
                                    <div key={i} className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner">
                                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{stat.label}</p>
                                        <p className="text-3xl font-black text-slate-900 font-outfit">{stat.val}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-4">
                                <h3 className="text-sm font-black text-slate-900 uppercase">Age Demographics</h3>
                                <div className="space-y-3">
                                    {[
                                        { l: 'Early Years (0-5)', v: 40 },
                                        { l: 'Adolescent (6-12)', v: 35 },
                                        { l: 'Tween / Teen (13+)', v: 25 },
                                    ].map((age, i) => (
                                        <div key={i} className="flex items-center gap-4">
                                            <span className="text-[10px] font-bold text-slate-500 w-32">{age.l}</span>
                                            <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                                                <div className="h-full bg-brand-600 rounded-full" style={{ width: `${age.v}%` }}></div>
                                            </div>
                                            <span className="text-[10px] font-black text-slate-900">{age.v}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <Button className="w-full h-14 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-xs uppercase tracking-widest mt-4 shadow-lg transition-all" onClick={() => setCaseloadModalOpen(false)}>
                                Close Breakdown
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}

function DashboardMetric({ label, value, icon, trend, color }) {
    return (
        <Card className="border-0 shadow-soft p-8 relative overflow-hidden rounded-[2.5rem] bg-white border border-slate-50 group hover:shadow-premium transition-all">
            <div className={`absolute -right-4 -top-4 w-28 h-28 rounded-full ${color} opacity-40 group-hover:scale-110 transition-transform duration-500`}></div>
            <div className="relative z-10">
                <div className={`h-16 w-16 rounded-[1.25rem] ${color} flex items-center justify-center mb-8 shadow-sm border border-white/50`}>
                    <div className="scale-125">{icon}</div>
                </div>
                <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-1.5">{label}</p>
                <div className="flex items-baseline gap-2">
                    <h3 className="text-4xl font-black text-slate-900 font-outfit tracking-tighter">{value}</h3>
                </div>
                <p className="text-[10px] font-bold text-slate-400 mt-3 flex items-center gap-1.5 uppercase tracking-tighter">
                    <TrendingUp className="h-3 w-3 text-brand-500" /> {trend}
                </p>
            </div>
        </Card>
    );
}
